import axios from 'axios'
import React, { useState } from 'react'
import { TextField, Button, Box, Typography, useTheme, useMediaQuery } from '@material-ui/core'
import Grid from '@material-ui/core/Grid'

import { secondaryFormatter } from './utils'
import UploadSection from './UploadSection'
import LastUploadedSection from '../components/LastUploadedSection'
import RegularColumnSelection from '../components/RegularColumnSelection'

import { hasAllRequiredColumnValues } from './SourceDataMethods'
import { makeStyles } from '@material-ui/core/styles'
import { API_URL } from '../../../config'
import { firstLetterUpper } from '../../../util/stringTransform'
import toast from 'react-hot-toast'
import { logError } from '../../../util/logger'
import RoundedButton from '../../../components/RoundedButton'
import SourceIcons from '../../../assets/icons'

const useStyles = makeStyles(() => ({
  breakLine: {
    margin: 10,
    marginTop: 20,
    marginBottom: 20,
  },
  root: {},
}))

const LeadTimesSourceData = ({ uploadSummary, refetchData, setExpand }) => {
  const classes = useStyles()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const currentColumnRef = uploadSummary?.columnRef
  const currentSheetSelected = uploadSummary?.sheetNameToSelect
  const currentUploadedFile = uploadSummary?.fileName
  const lastImported = uploadSummary?.lastImported
  const fileStructure = uploadSummary?.fileStructure
  const fileToken = uploadSummary?.fileToken
  const status = uploadSummary?.status
  const defaultLeadTimeValue = uploadSummary?.defaultLeadTime
  const statusMessage = uploadSummary?.statusMessage

  const [sheet, setSheet] = useState(currentSheetSelected || '')
  const [currentColumnList, setCurrentColumnList] = useState(currentSheetSelected ? secondaryFormatter(fileStructure, currentSheetSelected) : [])
  const [defaultLeadTime, setDefaultLeadTime] = useState(defaultLeadTimeValue || 4)
  const [vendorName, setVendorName] = useState(currentColumnRef?.vendorName || '')
  const [leadTime, setLeadTime] = useState(currentColumnRef?.leadTime || 4)
  const [partNumber, setPartNumber] = useState(currentColumnRef?.partNumber || '')
  const [disableSave, setDisableSave] = useState(true)
  const [isDefaultLeadTimeDirty, setIsDefaultLeadTimeDirty] = useState(false)
  const [isUpdating, setIsUpdating] = useState(false)

  const setColumnData = (inputSheet, sheet) => {
    setCurrentColumnList(secondaryFormatter(inputSheet.fileStructure, sheet))
  }

  const changeSheet = (sheetName) => {
    if (sheetName === '') {
      setCurrentColumnList([])
    } else setColumnData(uploadSummary, sheetName)
    setSheet(sheetName)
  }

  const criticalColumnConfig = [
    {
      label: 'Vendor Name',
      ref: vendorName,
      refSet: setVendorName,
      required: true,
    },
    {
      label: 'Lead Time',
      ref: leadTime,
      refSet: setLeadTime,
      required: true,
    },
    {
      label: 'Part Number',
      ref: partNumber,
      refSet: setPartNumber,
      required: true,
    },
  ]

  const allRequiredFieldsSelected = hasAllRequiredColumnValues(criticalColumnConfig)
  const handleFieldChange = (setStateFn, value) => {
    setStateFn(value)
    if (disableSave) setDisableSave(false)
  }

  const handleSaveDefaultLeadTime = async (e) => {
    e.preventDefault()
    const setVendorColumnRef = `${API_URL}/api/dataSource/v1/defaultLeadTime`
    const data = {
      fileToken: fileToken,
      defaultLeadTime: Number(defaultLeadTime),
    }

    setIsUpdating(false)

    const promise = axios.post(setVendorColumnRef, data)

    toast.promise(promise, {
      loading: 'Saving default lead time...',
      success: 'Default lead time saved!',
      error: 'Failed to save default lead time.',
    })

    await promise
    setIsDefaultLeadTimeDirty(false)
    setIsUpdating(false)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    const setVendorColumnRef = `${API_URL}/api/dataSource/v1/setLeadTimeColumnRef`
    const data = {
      fileToken: fileToken,
      cleanImport: true,
      sheetName: sheet,
      columnRef: {
        vendorName,
        leadTime,
        partNumber,
      },
      defaultLeadTime: Number(defaultLeadTime),
    }

    const updateVendorsColumnRef = axios
      .post(setVendorColumnRef, data)
      .then(async () => {
        await refetchData()
        setDisableSave(true)
        setExpand(false)
      })
      .catch((err) => {
        toast.error('Somethign went wrong while trying to save your new selection. Please try again')
        logError(err.message, err.stack)
      })

    toast.promise(updateVendorsColumnRef, {
      loading: <div>{`New Lead times! This will replace your old Lead times data.`}</div>,
      success: <b>Updated!</b>,
      error: <b>We had an issue saving your changes, please try again!</b>,
    })
  }

  return (
    <Grid container spacing={2} direction='column' className={classes.root}>
      <UploadSection uploadDataType='leadTimes' refetchSourceInfoFn={refetchData} setExpand={setExpand} uploadSummary={uploadSummary} />
      <hr className={classes.breakLine} />
      <LastUploadedSection
        fileName={currentUploadedFile}
        lastImported={lastImported}
        status={firstLetterUpper(status)}
        statusMessage={statusMessage}
        label={`Lead Times`}
      />

      <Grid
        container
        direction='row'
        justifyContent='flex-start'
        alignItems='center'
        spacing={2}
        style={{
          marginTop: 50,
        }}
      >
        <Grid item xs={12} lg={3}>
          <Typography className={classes.columnName}>
            Default Lead Time (weeks){' '}
            <img title='Used for parts without a specified lead time in the uploaded file.' style={{ width: '16px' }} src={SourceIcons.infoSvg} />
          </Typography>
        </Grid>
        <Grid item xs={12} lg={5}>
          <Box display='flex' flexDirection={isMobile ? 'column' : 'row'} alignItems='center' style={{ gap: '16px', maxWidth: 470 }}>
            <TextField
              type='number'
              value={defaultLeadTime}
              onChange={(e) => {
                setDefaultLeadTime(e.target.value)
                if (!isDefaultLeadTimeDirty) setIsDefaultLeadTimeDirty(true)
              }}
              variant='outlined'
              fullWidth
              className={classes.selectEmpty}
            />
            <RoundedButton
              onClick={handleSaveDefaultLeadTime}
              variant='contained'
              color='primary'
              type='submit'
              isLoading={isUpdating}
              disabled={!isDefaultLeadTimeDirty}
            >
              Save
            </RoundedButton>
          </Box>
        </Grid>
      </Grid>
      <hr className={classes.breakLine} />
      <RegularColumnSelection
        config={criticalColumnConfig}
        changeSheet={changeSheet}
        currentSheet={sheet}
        fileStructure={fileStructure}
        currentColumnList={currentColumnList}
        handleFieldChange={handleFieldChange}
        handleSubmit={handleSubmit}
        allRequiredFieldsSelected={allRequiredFieldsSelected}
        disableSave={disableSave}
      />
    </Grid>
  )
}

export default LeadTimesSourceData
